import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Collapse,
  IconButton,
  Alert,
  Divider,
  Tooltip,
  alpha
} from '@mui/material';
import { styled, useTheme } from '@mui/material/styles';
import {
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Info as InfoIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  CheckCircle as CheckCircleIcon
} from '@mui/icons-material';

// Styled components
const SectionPaper = styled(Paper)(({ theme, variant, hasErrors, isCompleted }) => {
  const getBorderColor = () => {
    if (hasErrors) return theme.palette.error.main;
    if (isCompleted) return theme.palette.success.main;
    return theme.palette.divider;
  };

  const getBackgroundColor = () => {
    if (variant === 'outlined') {
      return 'transparent';
    }
    if (hasErrors) {
      return alpha(theme.palette.error.main, 0.02);
    }
    if (isCompleted) {
      return alpha(theme.palette.success.main, 0.02);
    }
    return theme.palette.background.paper;
  };

  return {
    padding: theme.spacing(3),
    marginBottom: theme.spacing(2),
    border: variant === 'outlined' ? `1px solid ${getBorderColor()}` : 'none',
    backgroundColor: getBackgroundColor(),
    borderRadius: theme.shape.borderRadius * 2,
    position: 'relative',
    transition: 'all 0.3s ease',
    
    ...(hasErrors && {
      borderLeft: `4px solid ${theme.palette.error.main}`,
    }),
    
    ...(isCompleted && {
      borderLeft: `4px solid ${theme.palette.success.main}`,
    }),

    '&:hover': {
      boxShadow: theme.shadows[2],
    }
  };
});

const SectionHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'flex-start',
  marginBottom: theme.spacing(2),
  cursor: 'pointer',
  userSelect: 'none',
  
  '&:hover': {
    '& .section-title': {
      color: theme.palette.primary.main,
    }
  }
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 600,
  fontSize: '1.1rem',
  transition: 'color 0.2s ease',
  flex: 1,
  
  '&.section-title': {
    // Selector for hover effect
  }
}));

const SectionIcon = styled(Box)(({ theme, status }) => {
  const getColor = () => {
    switch (status) {
      case 'error':
        return theme.palette.error.main;
      case 'completed':
        return theme.palette.success.main;
      case 'warning':
        return theme.palette.warning.main;
      default:
        return theme.palette.primary.main;
    }
  };

  return {
    marginRight: theme.spacing(2),
    color: getColor(),
    display: 'flex',
    alignItems: 'center',
    transition: 'color 0.2s ease'
  };
});

const HelpTextContainer = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  padding: theme.spacing(1, 2),
  backgroundColor: alpha(theme.palette.info.main, 0.05),
  borderRadius: theme.shape.borderRadius,
  border: `1px solid ${alpha(theme.palette.info.main, 0.2)}`,
}));

/**
 * Form section container component with collapsible content and validation states
 * 
 * This component provides a standardized container for form sections with
 * header, help text, validation feedback, and collapsible content.
 * 
 * @param {Object} props - Component properties
 * @param {string} props.title - Section title
 * @param {string} props.description - Section description/help text
 * @param {React.ReactNode} props.icon - Section icon
 * @param {React.ReactNode} props.children - Section content
 * @param {boolean} props.collapsible - Whether section can be collapsed
 * @param {boolean} props.defaultExpanded - Default expanded state
 * @param {boolean} props.required - Whether section is required
 * @param {boolean} props.optional - Whether section is optional
 * @param {Array} props.errors - Array of error messages
 * @param {Array} props.warnings - Array of warning messages
 * @param {boolean} props.isCompleted - Whether section is completed
 * @param {string} props.variant - Visual variant ('default', 'outlined')
 * @param {string} props.helpText - Additional help text
 * @param {React.ReactNode} props.helpContent - Rich help content
 * @param {Function} props.onToggle - Callback when section is toggled
 * @param {string} props.testId - Test ID for testing
 * @param {Object} props.sx - Additional styles
 */
const FormSectionContainer = ({
  title,
  description,
  icon,
  children,
  collapsible = false,
  defaultExpanded = true,
  required = false,
  optional = false,
  errors = [],
  warnings = [],
  isCompleted = false,
  variant = 'default',
  helpText,
  helpContent,
  onToggle,
  testId,
  sx = {},
  ...props
}) => {
  const theme = useTheme();
  const [expanded, setExpanded] = useState(defaultExpanded);
  
  // Determine section status
  const hasErrors = errors.length > 0;
  const hasWarnings = warnings.length > 0;
  const sectionStatus = hasErrors ? 'error' : hasWarnings ? 'warning' : isCompleted ? 'completed' : 'default';
  
  /**
   * Handles section toggle
   */
  const handleToggle = () => {
    if (!collapsible) return;
    
    const newExpanded = !expanded;
    setExpanded(newExpanded);
    
    if (onToggle) {
      onToggle(newExpanded);
    }
  };
  
  /**
   * Renders the section icon based on status
   */
  const renderSectionIcon = () => {
    if (icon) {
      return (
        <SectionIcon status={sectionStatus}>
          {React.cloneElement(icon, { fontSize: 'small' })}
        </SectionIcon>
      );
    }
    
    // Default icons based on status
    let defaultIcon;
    switch (sectionStatus) {
      case 'error':
        defaultIcon = <ErrorIcon fontSize="small" />;
        break;
      case 'warning':
        defaultIcon = <WarningIcon fontSize="small" />;
        break;
      case 'completed':
        defaultIcon = <CheckCircleIcon fontSize="small" />;
        break;
      default:
        defaultIcon = <InfoIcon fontSize="small" />;
    }
    
    return (
      <SectionIcon status={sectionStatus}>
        {defaultIcon}
      </SectionIcon>
    );
  };
  
  /**
   * Renders validation messages
   */
  const renderValidationMessages = () => {
    if (!hasErrors && !hasWarnings) return null;
    
    return (
      <Box sx={{ mb: 2 }}>
        {errors.map((error, index) => (
          <Alert 
            key={`error-${index}`}
            severity="error" 
            sx={{ mb: 1, '&:last-child': { mb: 0 } }}
            variant="outlined"
          >
            {error}
          </Alert>
        ))}
        
        {warnings.map((warning, index) => (
          <Alert 
            key={`warning-${index}`}
            severity="warning" 
            sx={{ mb: 1, '&:last-child': { mb: 0 } }}
            variant="outlined"
          >
            {warning}
          </Alert>
        ))}
      </Box>
    );
  };
  
  /**
   * Renders help content
   */
  const renderHelpContent = () => {
    if (!helpText && !helpContent) return null;
    
    return (
      <HelpTextContainer>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
          <InfoIcon 
            sx={{ 
              color: 'info.main', 
              fontSize: '1rem', 
              mt: 0.25 
            }} 
          />
          <Box sx={{ flex: 1 }}>
            {helpText && (
              <Typography variant="body2" color="text.secondary">
                {helpText}
              </Typography>
            )}
            {helpContent && (
              <Box sx={{ mt: helpText ? 1 : 0 }}>
                {helpContent}
              </Box>
            )}
          </Box>
        </Box>
      </HelpTextContainer>
    );
  };
  
  return (
    <SectionPaper
      variant={variant}
      hasErrors={hasErrors}
      isCompleted={isCompleted}
      sx={sx}
      data-testid={testId}
      {...props}
    >
      {/* Section Header */}
      <SectionHeader onClick={handleToggle}>
        {renderSectionIcon()}
        
        <Box sx={{ flex: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <SectionTitle className="section-title" variant="h6">
              {title}
              {required && (
                <Typography 
                  component="span" 
                  sx={{ color: 'error.main', ml: 0.5 }}
                >
                  *
                </Typography>
              )}
            </SectionTitle>
            
            {optional && (
              <Typography 
                variant="caption" 
                sx={{ 
                  color: 'text.secondary',
                  backgroundColor: alpha(theme.palette.text.secondary, 0.1),
                  px: 1,
                  py: 0.25,
                  borderRadius: 1,
                  fontSize: '0.75rem'
                }}
              >
                Optional
              </Typography>
            )}
            
            {isCompleted && (
              <Tooltip title="Section completed">
                <CheckCircleIcon 
                  sx={{ 
                    color: 'success.main', 
                    fontSize: '1rem' 
                  }} 
                />
              </Tooltip>
            )}
          </Box>
          
          {description && (
            <Typography 
              variant="body2" 
              color="text.secondary"
              sx={{ mt: 0.5 }}
            >
              {description}
            </Typography>
          )}
        </Box>
        
        {collapsible && (
          <IconButton 
            size="small"
            sx={{ 
              color: 'text.secondary',
              transition: 'transform 0.2s ease',
              transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)'
            }}
          >
            <ExpandMoreIcon />
          </IconButton>
        )}
      </SectionHeader>
      
      {/* Validation Messages */}
      {renderValidationMessages()}
      
      {/* Help Content */}
      {renderHelpContent()}
      
      {/* Section Content */}
      {collapsible ? (
        <Collapse in={expanded} timeout="auto" unmountOnExit>
          <Box sx={{ pt: 1 }}>
            {children}
          </Box>
        </Collapse>
      ) : (
        <Box sx={{ pt: 1 }}>
          {children}
        </Box>
      )}
    </SectionPaper>
  );
};

export default FormSectionContainer;